import { useTranslate } from 'react-polyglot';
import { useTheme } from 'styled-components';

import type { QuestionAnalyticsBar } from '../../../../../../utils/buildQuestionAnalytics';
import {
  BarContent,
  BarCount,
  BarFill,
  BarHeader,
  BarImage,
  BarLabel,
  BarLetter,
  BarPercentage,
  BarRow,
  BarTitle,
  BarTrack,
  BarVotes,
  QuestionSubtitle,
  Wrapper,
} from './QuestionBars.styled';

interface QuestionBarsProps {
  bars: QuestionAnalyticsBar[];
  questionText: string;
  /** A rating scale reads as a gradient of values; a survey as one colour per option. */
  isRating?: boolean;
}

/**
 * The ramp a rating scale is painted with, from the lowest value up. A scale longer than
 * five values wraps around it rather than running out of colours.
 */
const RATING_SHADES = [1, 2, 4, 5, 7] as const;

/** The same alphabet the option's picture is stamped with on the slide. */
const OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Every option of a survey or every value of a scale, in the order the participant saw
 * them — document order, or ascending. Never sorted by votes: bars that swap places while
 * answers arrive cannot be read live, which is the whole point of showing them here.
 */
export const QuestionBars = ({
  bars,
  questionText,
  isRating = false,
}: QuestionBarsProps) => {
  const t = useTranslate();
  const { color } = useTheme();

  // A survey's options are not a scale: telling them apart is the label's job, so every
  // bar is the same colour and the lengths are what the eye compares.
  const fillColor = (index: number) =>
    color.background.complementary(
      isRating ? RATING_SHADES[index % RATING_SHADES.length] : 7,
    );

  return (
    <Wrapper>
      <QuestionSubtitle>{questionText}</QuestionSubtitle>

      {bars.map(({ id, label, imageUrl, optionPosition, votes, percentage }, index) => (
        <BarRow key={id}>
          {imageUrl && <BarImage src={imageUrl} alt={label ?? ''} />}

          <BarContent>
            <BarHeader>
              <BarTitle>
                {/* Only an image survey letters its options, and only it brings a picture. */}
                {imageUrl && (
                  <BarLetter as="span">
                    {OPTION_LETTERS[index % OPTION_LETTERS.length]}
                  </BarLetter>
                )}
                <BarLabel>
                  {label ??
                    t('__new.play.analyticsResults.option.unnamed', {
                      position: optionPosition,
                    })}
                </BarLabel>
              </BarTitle>
              {/* Plain text: a percentage and a count read the same in every language, and
                  this way the tests can assert on them. Spans rather than the headings
                  these typefaces are: a figure per bar is not a document outline. */}
              <BarCount>
                <BarPercentage as="span">{`${percentage}%`}</BarPercentage>
                <BarVotes as="span">{`· ${votes}`}</BarVotes>
              </BarCount>
            </BarHeader>

            <BarTrack>
              <BarFill $percentage={percentage} $color={fillColor(index)} />
            </BarTrack>
          </BarContent>
        </BarRow>
      ))}
    </Wrapper>
  );
};
